import { useQuery } from '@tanstack/react-query';
import { RefreshCw } from 'lucide-react';

import { apiClient } from '@/api/client';
import { MainLayout } from '@/components/layout/MainLayout';
import { Button, Card, CardContent } from '@/components/ui';

type BillingPolicy = {
  metric: string;
  enabled: boolean;
  warning_threshold: number | null;
  critical_threshold: number | null;
  reminder_interval: number;
};

type BillingProfile = {
  enabled: boolean;
  wazzup_partner_account_id: string;
};

type BillingConfiguration = {
  tenant_id: string;
  tenant_slug: string;
  profile: BillingProfile;
  policies: BillingPolicy[];
};

type BillingConfigurationsResponse = {
  items: BillingConfiguration[];
};

export function BillingMonitorPage() {
  const query = useQuery({
    queryKey: ['admin', 'billing-monitor'],
    queryFn: async () => {
      const response = await apiClient.get<BillingConfigurationsResponse>('/admin/billing-monitor/');
      return response.data;
    },
  });

  return (
    <MainLayout headerTitle="Операции · Балансы и подписки">
      <div className="max-w-6xl space-y-6 p-6">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-sm text-neutral-600">
              Подписка: предупреждение за 7 дней и критично за 3 дня. Баланс WABA: предупреждение от 500 ₽, критично от 100 ₽.
              Алерты уходят Георгию в бот Улыбки. Профиль клиники включается только с 8-значным account ID. Секреты не показываются.
            </p>
          </div>
          <Button variant="secondary" onClick={() => void query.refetch()} isLoading={query.isFetching}>
            <RefreshCw className="mr-2 h-4 w-4" />
            Обновить
          </Button>
        </div>

        {query.isLoading && <Card><CardContent className="p-6 text-neutral-600">Загрузка…</CardContent></Card>}
        {query.isError && (
          <Card><CardContent className="p-6 text-red-700">
            Не удалось загрузить конфигурацию. Проверьте права superadmin и доступность API.
          </CardContent></Card>
        )}
        {!query.isLoading && !query.isError && query.data?.items.length === 0 && (
          <Card><CardContent className="p-6 text-neutral-600">Активные tenants не найдены.</CardContent></Card>
        )}
        {query.data?.items.map((item) => (
          <Card key={item.tenant_id}>
            <CardContent className="space-y-3 p-6">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h2 className="font-semibold text-neutral-900">{item.tenant_slug}</h2>
                <span className={`rounded px-2 py-1 text-xs font-medium ${item.profile.enabled ? 'bg-emerald-50 text-emerald-700' : 'bg-neutral-100 text-neutral-600'}`}>
                  {item.profile.enabled ? 'Мониторинг включён' : 'Мониторинг выключен'}
                </span>
              </div>
              <p className="text-sm text-neutral-600">
                Wazzup Partner account ID: {item.profile.wazzup_partner_account_id || 'не задан'}
              </p>
              <ul className="space-y-1 text-sm text-neutral-700">
                {item.policies.map((policy) => (
                  <li key={policy.metric}>
                    {policy.metric}: {policy.enabled ? 'включена' : 'выключена'} · warning {policy.warning_threshold ?? '—'} · critical {policy.critical_threshold ?? '—'}
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        ))}
      </div>
    </MainLayout>
  );
}
